import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectSettings, selectSettingsLoading, selectSettingsError } from '../store/selectors';
import { fetchSettings } from '../store/thunks/settingsThunks';
import { useSession } from './useSession';
import { getCurrentLanguage } from '../constants/localization';

/**
 * Hook to access settings from Redux store with optional automatic fetching
 */
export const useSettingsRedux = (autoFetch: boolean = true) => {
  const dispatch = useAppDispatch();
  const { sessionId, getFormattedExperiments, experimentsLoaded } = useSession();

  const settings = useAppSelector(selectSettings);
  const loading = useAppSelector(selectSettingsLoading);
  const error = useAppSelector(selectSettingsError);

  // Auto-fetch settings when session and experiments are available and we don't have settings yet
  useEffect(() => {
    if (autoFetch && sessionId && experimentsLoaded && !settings && !loading) {
      const experiments = getFormattedExperiments();
      dispatch(
        fetchSettings({
          language: getCurrentLanguage(),
          experiments,
          sessionId: sessionId || undefined,
        })
      );
    }
  }, [dispatch, sessionId, experimentsLoaded, settings, loading, autoFetch, getFormattedExperiments]);

  return {
    settings,
    loading,
    error,
    // Helper to manually refetch settings
    refetch: () => {
      if (sessionId && experimentsLoaded) {
        const experiments = getFormattedExperiments();
        dispatch(
          fetchSettings({
            language: getCurrentLanguage(),
            experiments,
            sessionId: sessionId || undefined,
          })
        );
      }
    },
  };
};
